import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  InfoOutlined,
  NotificationsActive,
  Settings,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';

import LoadingTagReminder from '../../pages/settings/TaskPage/reminders/components/LoadingTagReminder';
import TagReminder from '../../pages/settings/TaskPage/reminders/components/TagReminder';
import { getTagRemindersForTag } from '../../redux/tagReminder/actions';
import { grey } from '../../utils/brandColors';

const HorseDueDatesComponent = ({
  classes,
  barnHorse,
  isAdmin,
  health,
  zIndex,
}) => {
  const dispatch = useDispatch();

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const tagId = Number(barnHorse && barnHorse.pingumTagId);
  const reminderState = useSelector((state) => (state.tagReminder.tagRemindersByTag || {})[tagId]);
  const tagTagReminders = (reminderState && reminderState.content) || [];

  const loadData = React.useCallback(() => {
    if (tagId) {
      return dispatch(getTagRemindersForTag(tagId)).catch(() => {});
    }
    return Promise.resolve();
  }, [dispatch, tagId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const reminders = tagTagReminders
    .filter((reminder) => Number(reminder.tag.id) === tagId)
    .filter((reminder) => !health || (reminder.eventTemplateReminder.eventTemplate.tags || [])
      .some((tag) => tag.tagType && tag.tagType.id === selectedBarn.barnMetadata.healthTagTypeId))
    .sort((a, b) => a.eventTemplateReminder.eventTemplate.name.localeCompare(b.eventTemplateReminder.eventTemplate.name));
  const loading = !!tagId && (!reminderState || reminderState.isFetching);
  const visibleError = reminderState && reminderState.error;
  const heading = health
    ? 'Health Reminders'
    : 'Reminders';
  const emptyMessage = health
    ? 'No health reminders are set up for this horse.'
    : 'No reminders are set up for this horse.';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar><NotificationsActive /></Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {heading}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ color: 'text.secondary' }}
            >
              <InfoOutlined sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                Manage which tasks are associated with reminders in the reminder settings page.
              </Typography>
            </Stack>
          </Box>
        </Box>

        {
          isAdmin && (
            <Tooltip title="You can manage what tasks are associated with reminders for horses in the reminder settings page">
              <Button startIcon={<Settings />} size="small" href="/settings/tasks/reminders">
                Manage Reminders
              </Button>
            </Tooltip>
          )
        }
      </Stack>
      {visibleError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={loadData}>Retry</Button>}>
          {visibleError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small" className={classes.table}>
          <TableBody>
            {
            loading && [1, 2, 3, 4, 5].map((item) => (
              <LoadingTagReminder key={`loading-item-${item}`} classes={classes} includeHorse={false} />
            ))
          }
            {
            (!loading && !visibleError) && (
              reminders.map((tr) => (
                <TagReminder
                  key={`task-item-${tr.id}`}
                  tagReminder={tr}
                  fullScreen={false}
                  includeHorse={false}
                  includeTask
                  condensed
                  triggerDataRefresh={loadData}
                  zIndex={zIndex}
                />

              ))
            )
          }
          </TableBody>
        </Table>
      </TableContainer>
      {
        !loading && !visibleError && reminders.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <InfoOutlined
              sx={{
                fontSize: '3.5rem',
                color: grey[400],
                m: 2,
              }}
            />
            <Typography sx={{ fontWeight: 300 }}>
              {emptyMessage}
            </Typography>
          </Box>
        )
      }
    </Box>
  );
}

HorseDueDatesComponent.propTypes = {
  classes: PropTypes.object,
  barnHorse: PropTypes.object,
  isAdmin: PropTypes.bool,
  health: PropTypes.bool,
  zIndex: PropTypes.number,
};
HorseDueDatesComponent.defaultProps = {
  classes: {},
  barnHorse: null,
  isAdmin: false,
  health: false,
  zIndex: undefined,
};

export default (HorseDueDatesComponent);
